import { useSuspenseQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { type ReactNode, Suspense } from 'react';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import type { NavigationScreenOpenAPISpecState } from '@gitbook/app-navigation';
import { DateShortRelative } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Alert,
    BreadcrumbButton,
    BreadcrumbItem,
    BreadcrumbSpan,
    Breadcrumbs,
    Button,
    type ButtonProps,
    ButtonSkeleton,
    Card,
    CardBody,
    Description,
    DescriptionDetail,
    DescriptionList,
    DescriptionListIcon,
    DescriptionTerm,
    DialogRoot,
    DialogTrigger,
    EmptyState,
    EmptyStateIcon,
    EmptyStateNotFoundHeading,
    FieldGroup,
    HeaderBar,
    HeaderBarActions,
    HeaderBarActionsMain,
    HeaderBarActionsSide,
    HeaderBarBreadcrumbs,
    HeaderBarMain,
    Heading,
    HoverCard,
    HoverCardRoot,
    HoverCardTrigger,
    type IconDefinition,
    IconSkeleton,
    Label,
    Layout,
    Link,
    LoadingIndicator,
    Page,
    PageWallpaper,
    renderIcon,
    Separator,
    Switch,
    TextSkeleton,
    TooltipIndicator,
    toast,
} from '@gitbook/spine-react';
import { ErrorBoundary } from '@gitbook/util-debug';
import { HeadTitle } from '@gitbook/util-react-head';
import { useLoadingState } from '@gitbook/util-react-hooks';
import { assert, assertNever } from '@gitbook/util-ts';

import { bgGradient } from '../../assets';
import {
    APIErrorBoundary,
    CopyableURL,
    checkIsValidFilesystem,
    confirmOpenAPISpecDelete,
    getOpenAPIProcessingErrorMessage,
    OpenAPIShareFeedbackButton,
    OpenAPISpecEditDialog,
    OpenAPISpecEditor,
    OpenAPISpecSelector,
    OpenAPIWaitForSpec,
    OrganizationMissingPermRedirect,
} from '../../Components';
import {
    NavigationScreenWithOrganization,
    useNavigation,
    useSetAnalyticsScreen,
} from '../../navigation';
import { apiClient, apiQuery } from '../../state';
import { createDocumentationURL } from '../../utils';
import { OpenAPISpecActionsButton } from './OpenAPISpecActionsButton';
import { checkIsOpenAPISpecProcessing } from './util';

export function OpenAPISpecScreen(props: { screen: NavigationScreenOpenAPISpecState }) {
    return (
        <NavigationScreenWithOrganization screen={props.screen}>
            {({ screen }) => (
                <APIErrorBoundary
                    resetKeys={[screen.spec]}
                    fallback={{
                        404: <OpenAPISpecNotFound screen={screen} />,
                        403: (
                            <OrganizationMissingPermRedirect
                                title="Error while accessing OpenAPI specification"
                                organizationId={screen.organization}
                            />
                        ),
                    }}
                >
                    <HeadTitle title={`${screen.spec} specification`} />
                    <Suspense
                        fallback={
                            <OpenAPISpecPageLayout openAPISpec={null} screen={screen}>
                                <OpenAPISpecPageContentLayout
                                    summary={<OpenAPISpecSummarySkeleton screen={screen} />}
                                    editor={<OpenAPISpecEditorFallback />}
                                />
                            </OpenAPISpecPageLayout>
                        }
                    >
                        <OpenAPISpecScreenWithOrganization screen={screen} />
                    </Suspense>
                </APIErrorBoundary>
            )}
        </NavigationScreenWithOrganization>
    );
}

type NavigationScreenOpenAPISpecStateWithOrg = NavigationScreenOpenAPISpecState & {
    organization: string;
};

function OpenAPISpecScreenWithOrganization(props: {
    screen: NavigationScreenOpenAPISpecStateWithOrg;
}) {
    const { screen } = props;
    const organizationId = screen.organization;
    const specSlug = screen.spec;

    const { data: openAPISpec } = useSuspenseQuery(
        apiQuery('getOpenAPISpecBySlug', {
            organizationId,
            specSlug,
        })
    );

    if (!openAPISpec.lastVersion) {
        return (
            <OpenAPISpecPageLayout openAPISpec={openAPISpec} screen={screen}>
                <TrackAnalyticsScreen organizationId={screen.organization} />
                <OpenAPIWaitForSpec organizationId={organizationId} specSlug={specSlug} />
            </OpenAPISpecPageLayout>
        );
    }

    return (
        <OpenAPISpecPageLayout openAPISpec={openAPISpec} screen={screen}>
            <TrackAnalyticsScreen organizationId={screen.organization} />
            <OpenAPISpecPageContentLayout
                summary={
                    <>
                        <OpenAPISpecSummary openAPISpec={openAPISpec} screen={screen} />
                        {openAPISpec.lastProcessedErrors ? (
                            <OpenAPISpecErrors errors={openAPISpec.lastProcessedErrors} />
                        ) : null}
                    </>
                }
                editor={
                    <Suspense fallback={<OpenAPISpecEditorFallback />}>
                        <OpenAPISpecEditorFetch openAPISpec={openAPISpec} screen={screen} />
                    </Suspense>
                }
            />
        </OpenAPISpecPageLayout>
    );
}

function OpenAPISpecPageContentLayout(props: { summary: ReactNode; editor: ReactNode }) {
    return (
        <Layout wide>
            <main className="grid grid-cols-6 gap-8 py-[--page-py] [--page-py:theme(spacing.6)] lg:[--page-py:theme(spacing.10)]">
                <div className="col-span-6 flex flex-col gap-4 lg:col-span-3 xl:col-span-2">
                    {props.summary}
                </div>
                <div className="col-span-6 lg:col-span-3 xl:col-span-4">{props.editor}</div>
            </main>
        </Layout>
    );
}

function OpenAPISpecSummary(props: {
    openAPISpec: api.OpenAPISpec;
    screen: NavigationScreenOpenAPISpecStateWithOrg;
}) {
    const { openAPISpec, screen } = props;
    return (
        <OpenAPISpecSummaryTemplate screen={screen}>
            <DescriptionList>
                <DescriptionTerm>Source</DescriptionTerm>
                {openAPISpec.sourceURL ? (
                    <DescriptionDetail>
                        <DescriptionListIcon icon={Icon.Globe} />
                        <CopyableURL url={openAPISpec.sourceURL} />
                    </DescriptionDetail>
                ) : (
                    <DescriptionDetail>
                        <DescriptionListIcon icon={Icon.FileCode} />
                        File
                    </DescriptionDetail>
                )}
            </DescriptionList>
            <DescriptionList>
                <DescriptionTerm>
                    Last updated
                    {openAPISpec.sourceURL ? (
                        <DescriptionListIcon
                            icon={
                                <TooltipIndicator label="GitBook checks for updates automatically every 6 hours." />
                            }
                        />
                    ) : null}
                </DescriptionTerm>
                <DescriptionDetail>
                    <OpenAPIProcessStatus openAPISpec={openAPISpec} />
                </DescriptionDetail>
            </DescriptionList>
            <div className="-mx-6 flex">
                <Separator />
            </div>
            {!openAPISpec.sourceURL ? (
                <>
                    <OpenAPISpecPublishToggle
                        organizationId={screen.organization}
                        openAPISpec={openAPISpec}
                    />
                    <div className="-mx-6 flex">
                        <Separator />
                    </div>
                </>
            ) : null}

            <div className="text-center text-muted text-ui-small">
                <p className="mb-4">
                    You can use the spec to generate a complete API reference or OpenAPI blocks in
                    any space.
                </p>
                <Link
                    label="Learn how to generate an API reference"
                    trailingIcon={Icon.ExternalLink}
                    href={createDocumentationURL(
                        'api-references/openapi/insert-api-reference-in-your-docs'
                    )}
                    target="_blank"
                />
            </div>
        </OpenAPISpecSummaryTemplate>
    );
}

function OpenAPISpecErrors(props: { errors: api.OpenAPIErrorObject[] }) {
    const { errors } = props;
    return (
        <Card className="mt-2 p-4 text-left">
            <h2 className="mb-2 flex items-center gap-2 text-ui-heading-small">
                <Icon.Warning
                    className={clsx('-mt-[0.1em] size-4', errors.length > 0 && 'text-warning')}
                />
                Anomalies
            </h2>
            {errors.length === 0 ? (
                <div className="flex items-center gap-2 text-muted">No anomalies detected</div>
            ) : (
                <ul className="list-inside list-disc">
                    {errors.map((error, index) => (
                        <li key={index}>{error.message}</li>
                    ))}
                </ul>
            )}
        </Card>
    );
}

function OpenAPIProcessStatus(props: { openAPISpec: api.OpenAPISpec }) {
    const { openAPISpec } = props;
    switch (openAPISpec.processingState) {
        case api.OpenAPISpecProcessingState.Pending:
        case api.OpenAPISpecProcessingState.Progress: {
            return (
                <>
                    <DescriptionListIcon icon={Icon.Reset} />
                    Updating…
                </>
            );
        }
        case api.OpenAPISpecProcessingState.Complete: {
            assert(
                openAPISpec.lastProcessedAt,
                'When processing state is complete, lastProcessedAt should be set'
            );
            if (openAPISpec.lastProcessErrorCode) {
                return (
                    <>
                        <DescriptionListIcon icon={Icon.X} />
                        Error
                        <DateShortRelative
                            onlyDate={false}
                            value={new Date(openAPISpec.lastProcessedAt)}
                        />
                        <TooltipIndicator
                            label={getOpenAPIProcessingErrorMessage(
                                openAPISpec.lastProcessErrorCode
                            )}
                        />
                    </>
                );
            }
            return (
                <>
                    <DescriptionListIcon icon={Icon.Reset} />
                    <DateShortRelative
                        onlyDate={false}
                        value={new Date(openAPISpec.lastProcessedAt)}
                    />
                </>
            );
        }
        default:
            assertNever(openAPISpec.processingState);
    }
}

const EDITOR_CLASSNAME =
    'h-[calc(100vh_-_var(--app-unavailable-height)_-_var(--page-py)_*_2)] overflow-hidden rounded border-card bg-base shadow rounded-lg';

function OpenAPISpecEditorFetch(props: {
    openAPISpec: api.OpenAPISpec;
    screen: NavigationScreenOpenAPISpecStateWithOrg;
}) {
    const { screen, openAPISpec } = props;
    const { navigate } = useNavigation();
    const { data } = useSuspenseQuery(
        apiQuery('getLatestOpenAPISpecVersionContent', {
            organizationId: screen.organization,
            specSlug: screen.spec,
        })
    );

    const { filesystem } = data;

    const invalidAlert = (
        <Alert
            kind="warning"
            title="Invalid OpenAPI spec"
            details="This OpenAPI spec is invalid, delete it and create a new one."
            action={{
                label: 'Delete specification',
                onPress: () => {
                    void confirmOpenAPISpecDelete({
                        openAPISpec,
                        organizationId: screen.organization,
                        navigate,
                    });
                },
            }}
        />
    );
    if (!checkIsValidFilesystem(filesystem)) {
        return invalidAlert;
    }

    return (
        <ErrorBoundary fallback={invalidAlert}>
            <OpenAPISpecEditor
                name={screen.spec}
                filesystem={filesystem}
                className={EDITOR_CLASSNAME}
            />
        </ErrorBoundary>
    );
}

function OpenAPISpecEditorFallback() {
    return (
        <div className={clsx(EDITOR_CLASSNAME, 'flex items-center justify-center')}>
            <LoadingIndicator size="large" />
        </div>
    );
}

function Property(props: {
    term: {
        trailingIcon?: IconDefinition;
        label: ReactNode;
    };
    definition: {
        leadingIcon: IconDefinition;
        value: ReactNode;
    };
}) {
    return (
        <dl>
            <dt className="flex items-center gap-1.5 text-muted">
                {props.term.label}
                {renderIcon(props.term.trailingIcon, { className: 'size-3 shrink-0' })}
            </dt>
            <dd className="flex items-center gap-1.5">
                {renderIcon(props.definition.leadingIcon, { className: 'size-3 shrink-0' })}
                {props.definition.value}
            </dd>
        </dl>
    );
}

function OpenAPISpecSummarySkeleton(props: { screen: NavigationScreenOpenAPISpecStateWithOrg }) {
    const { screen } = props;
    return (
        <OpenAPISpecSummaryTemplate screen={screen}>
            <Property
                term={{ label: 'Source' }}
                definition={{
                    leadingIcon: <IconSkeleton />,
                    value: <TextSkeleton>File</TextSkeleton>,
                }}
            />
            <Property
                term={{ label: 'Last updated' }}
                definition={{
                    leadingIcon: <IconSkeleton />,
                    value: <TextSkeleton>10h ago</TextSkeleton>,
                }}
            />
        </OpenAPISpecSummaryTemplate>
    );
}

function OpenAPISpecSummaryTemplate(props: {
    screen: NavigationScreenOpenAPISpecStateWithOrg;
    children: ReactNode;
}) {
    const { screen, children } = props;
    return (
        <Card className="flex flex-col gap-4 px-6 py-4">
            <h1 className="text-ui-heading-large">{screen.spec}</h1>
            {children}
        </Card>
    );
}

function OpenAPISpecPublishToggle(props: { organizationId: string; openAPISpec: api.OpenAPISpec }) {
    const { organizationId, openAPISpec } = props;
    const [loading, wrapLoading] = useLoadingState();

    return (
        <div className="flex flex-col gap-2">
            <FieldGroup orientation="horizontal">
                <FieldGroup orientation="vertical">
                    <div className="flex items-center gap-1.5">
                        <Label>Public</Label>
                        <HoverCardRoot>
                            <HoverCardTrigger>
                                <div className="flex items-center gap-1.5 text-muted hover:text-base">
                                    <Icon.InfoCircle className="size-3 shrink-0" />
                                </div>
                            </HoverCardTrigger>
                            <HoverCard className="max-w-80">
                                <Card>
                                    <CardBody>
                                        <Heading>Make your OpenAPI spec public</Heading>
                                        <Description>
                                            Enable this to make your spec publicly accessible. A
                                            shareable URL to the latest version will appear below
                                            for use with external tools.
                                        </Description>
                                    </CardBody>
                                </Card>
                            </HoverCard>
                        </HoverCardRoot>
                    </div>
                    <Description className="font-normal text-muted">
                        Enable public, read-only access to this spec.
                    </Description>
                </FieldGroup>
                <Switch
                    value={openAPISpec.visibility === api.OpenAPISpecVisibility.Public}
                    loading={loading}
                    onValueChange={async (published: boolean) => {
                        await wrapLoading(async () => {
                            await apiClient.openapi.updateOpenApiSpecBySlug(
                                organizationId,
                                openAPISpec.slug,
                                {
                                    visibility: published
                                        ? api.OpenAPISpecVisibility.Public
                                        : api.OpenAPISpecVisibility.Private,
                                }
                            );
                        });
                    }}
                />
            </FieldGroup>
            {openAPISpec.urls.published ? (
                <div className="flex items-center gap-1.5">
                    <DescriptionListIcon icon={Icon.Globe} />
                    <CopyableURL url={openAPISpec.urls.published} when="always" />
                </div>
            ) : null}
        </div>
    );
}

function EditButton(
    props: { openAPISpec: api.OpenAPISpec; organizationId: string } & Pick<
        ButtonProps,
        'label' | 'kind'
    >
) {
    const { organizationId, openAPISpec, label, kind } = props;
    return (
        <DialogRoot
            onOpenChange={(isOpen) => {
                if (isOpen) {
                    analytics.logEvent({
                        type: 'openapi_spec_edit_intent',
                        organization: organizationId,
                        specSlug: openAPISpec.slug,
                        triggerLocation: 'header',
                        trigger: 'main-action',
                    });
                }
            }}
        >
            <DialogTrigger>
                <Button leadingIcon={Icon.FileUpload} kind={kind} label={label} />
            </DialogTrigger>
            <OpenAPISpecEditDialog openAPISpec={openAPISpec} organizationId={organizationId} />
        </DialogRoot>
    );
}

/**
 * Check for updates button.
 */
function CheckForUpdatesButton(props: { openAPISpec: api.OpenAPISpec; organizationId: string }) {
    const { organizationId, openAPISpec } = props;
    const { sourceURL } = openAPISpec;
    assert(sourceURL, 'OpenAPI spec must have a source URL to sync from');
    const isProcessing = checkIsOpenAPISpecProcessing(openAPISpec);
    return (
        <Button
            leadingIcon={Icon.Refresh}
            label="Check for updates"
            loadable={isProcessing ? [isProcessing] : undefined}
            tooltip={isProcessing ? { label: 'Specification is updating...' } : undefined}
            onPress={async () => {
                await toast.catch(async () => {
                    await apiClient.openapi.createOrUpdateOpenApiSpecBySlug(
                        organizationId,
                        openAPISpec.slug,
                        { source: { url: sourceURL } }
                    );
                    analytics.logEvent({
                        type: 'openapi_spec_check_for_updates',
                        organization: organizationId,
                        specSlug: openAPISpec.slug,
                    });
                }, 'Error while checking for updates');
            }}
        />
    );
}

/**
 * Template of a page.
 */
function OpenAPISpecPageLayout(props: {
    screen: NavigationScreenOpenAPISpecStateWithOrg;
    openAPISpec: api.OpenAPISpec | null;
    children: ReactNode;
}) {
    const { screen, children, openAPISpec } = props;
    const { navigate } = useNavigation();
    return (
        <>
            <HeaderBar>
                <HeaderBarMain>
                    <HeaderBarBreadcrumbs>
                        <Breadcrumbs>
                            <BreadcrumbItem icon={Icon.OpenAPI} label="OpenAPI" />
                            <BreadcrumbItem
                                href={{
                                    screen: {
                                        type: 'openapi_spec',
                                        organization: screen.organization,
                                        spec: screen.spec,
                                    },
                                }}
                            >
                                {() => (
                                    <Suspense
                                        fallback={
                                            <BreadcrumbSpan isCurrent>{screen.spec}</BreadcrumbSpan>
                                        }
                                    >
                                        <OpenAPISpecSelector
                                            organizationId={screen.organization}
                                            onChange={(spec) => {
                                                navigate({
                                                    screen: {
                                                        type: 'openapi_spec',
                                                        organization: screen.organization,
                                                        spec: spec.slug,
                                                    },
                                                });
                                            }}
                                            analyticsContext={{
                                                triggerLocation: 'openapi-screen-breadcrumb',
                                            }}
                                        >
                                            {({ triggerProps }) => (
                                                <BreadcrumbButton
                                                    icon={Icon.ChevronDown}
                                                    isActive={true}
                                                    {...triggerProps}
                                                >
                                                    {screen.spec}
                                                </BreadcrumbButton>
                                            )}
                                        </OpenAPISpecSelector>
                                    </Suspense>
                                )}
                            </BreadcrumbItem>
                        </Breadcrumbs>
                    </HeaderBarBreadcrumbs>
                    {openAPISpec && (
                        <OpenAPISpecActionsButton
                            openAPISpec={openAPISpec}
                            organizationId={screen.organization}
                        />
                    )}
                </HeaderBarMain>
                <HeaderBarActions>
                    <HeaderBarActionsSide>
                        <OpenAPIShareFeedbackButton />
                    </HeaderBarActionsSide>
                    <HeaderBarActionsMain>
                        {openAPISpec ? (
                            openAPISpec.sourceURL ? (
                                <CheckForUpdatesButton
                                    openAPISpec={openAPISpec}
                                    organizationId={screen.organization}
                                />
                            ) : (
                                <EditButton
                                    openAPISpec={openAPISpec}
                                    organizationId={screen.organization}
                                    label="Update"
                                />
                            )
                        ) : (
                            <ButtonSkeleton label="Update" />
                        )}
                    </HeaderBarActionsMain>
                </HeaderBarActions>
            </HeaderBar>
            <Page className="flex flex-1 flex-col">
                <PageWallpaper
                    imageLight={bgGradient.light}
                    imageDark={bgGradient.dark}
                    className="flex-1 bg-[length:auto] bg-top"
                >
                    {children}
                </PageWallpaper>
            </Page>
        </>
    );
}

/**
 * Display a not found page for OpenAPI specs.
 */
function OpenAPISpecNotFound(props: { screen: NavigationScreenOpenAPISpecStateWithOrg }) {
    const { screen } = props;
    return (
        <OpenAPISpecPageLayout screen={screen} openAPISpec={null}>
            <EmptyState>
                <EmptyStateIcon>
                    <Icon.OpenAPI />
                </EmptyStateIcon>
                <EmptyStateNotFoundHeading />
                <Description>
                    The OpenAPI specification you are looking for does not exist.
                </Description>
            </EmptyState>
        </OpenAPISpecPageLayout>
    );
}

/**
 * Track a page view on the current screen.
 */
function TrackAnalyticsScreen(props: { organizationId: string }) {
    useSetAnalyticsScreen(
        {
            organization: props.organizationId,
            screen: 'openapi_spec',
        },
        [props.organizationId]
    );
    return null;
}
